import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { User, CheckCircle2, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { createUser } from "../../redux/slices/userSlice";
import { sendWelcomeMessage } from "../../services/whatsappService";
import { getAllUsers } from "../../services/adminRechargeService";
import AdminCard from "../../components/common/AdminCard";
import AdminInput from "../../components/common/AdminInput";
import { CustomDropdown } from "../../components/common/CustomDropdown";

const CreateUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Helper to convert string to Title Case
  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const { isLoading } = useSelector((state) => state.user);

  const [allUsers, setAllUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    whatsappNumber: "",
    address: "",
    email: "",
    password: "",
    confirmPassword: "",
    // Daily Records
    weight: "",
    fat: "",
    visceralFat: "",
    muscles: "",
    bmr: "",
    bmi: "",
    bodyAge: "",
    chest: "",
    waistLine: "",
    hip: "",
    // Account
    role: "user",
    customerType: "Trial",
    referredBy: "",
    sponsoredBy: "",
    referralType: "",
    purpose: "",
    // Herbalife Details
    herbalifeId: "",
    herbalifeLastName: "",
    herbalifeEmail: "",
    herbalifePassword: "",
    preferredCustomerStatus: "",
    herbalifeJoinYear: "",
    sponsorName: "",
    sponsorLastName: "",
    sponsorId: "",
    // Family & Important Dates
    birthday: "",
    maritalStatus: "",
    spouseName: "",
    marriageDate: "",
    children: [],
    height: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mobileExists, setMobileExists] = useState(false);

  const [showReferredByDropdown, setShowReferredByDropdown] = useState(false);
  const [showSponsoredByDropdown, setShowSponsoredByDropdown] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await getAllUsers();
        setAllUsers(users);
      } catch (error) {
        console.error("Failed to fetch users for autocomplete", error);
      }
    };
    fetchUsers();
  }, []);

  const getFilteredUsers = (searchTerm) => {
    if (!searchTerm) return allUsers.slice(0, 50); // Show top 50 users if no search term
    return allUsers.filter(u => 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      u.customerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.mobile?.includes(searchTerm)
    ).slice(0, 50);
  };

  // const fetchUsers = async () => { ... } 

  // Real-time duplicate mobile check
  const handleMobileChange = (value) => {
    handleChange("mobile", value);
    if (value.length === 10) {
      const duplicate = allUsers.find(
        (u) =>
          (u.mobile || u.personalInformation?.mobileNumber || u.mobileNumber || "") === value
      );
      setMobileExists(!!duplicate);
      if (duplicate) {
        setErrors((prev) => ({
          ...prev,
          mobile: `Already registered — ${duplicate.name || "existing user"}`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, mobile: "" }));
      }
    } else {
      setMobileExists(false);
      setErrors((prev) => ({ ...prev, mobile: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    // Mobile validation
    if (!formData.mobile || formData.mobile.trim() === "") {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    } else {
      // Duplicate check
      const duplicate = allUsers.find(
        (u) =>
          (u.mobile || u.personalInformation?.mobileNumber || u.mobileNumber || "") ===
          formData.mobile.trim()
      );
      if (duplicate) {
        newErrors.mobile = `This mobile number is already registered to ${duplicate.name || "another user"}`;
      }
    }

    // WhatsApp number validation (optional but if provided should be valid)
    if (
      formData.whatsappNumber &&
      !/^[6-9]\d{9}$/.test(formData.whatsappNumber)
    ) {
      newErrors.whatsappNumber =
        "Please enter a valid 10-digit WhatsApp number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddChild = () => {
    setFormData((prev) => ({
      ...prev,
      children: [...prev.children, { name: "", gender: "son", birthday: "" }],
    }));
  };

  const handleRemoveChild = (index) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index),
    }));
  };

  const handleChildChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.map((child, i) =>
        i === index ? { ...child, [field]: value } : child
      ),
    }));
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please check the highlighted fields");
      return;
    }

    // Final safety guard against duplicate mobile
    if (mobileExists) {
      toast.error("This mobile number is already registered. Please use a different number.");
      return;
    }

    try {
      const userData = {
        // All fields at root level for createUser action
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        whatsappNumber: formData.whatsappNumber.trim() || formData.mobile.trim(),
        email: formData.email.trim() || "",
        password: formData.password || formData.mobile || "123456",
        address: formData.address.trim(),
        role: formData.role,
        customerType: formData.customerType,
        purpose: formData.purpose || "",
        referredBy: toTitleCase(formData.referredBy) || "",
        sponsoredBy: toTitleCase(formData.sponsoredBy) || "", // New field
        referralInput: formData.referralType || "",
        herbalifeId: formData.herbalifeId.trim(),
        preferredStatus: formData.preferredCustomerStatus.trim(),
        herbalifeLastName: formData.herbalifeLastName.trim(),
        joinYear: formData.herbalifeJoinYear.trim(),
        herbalifeEmail: formData.herbalifeEmail.trim(),
        herbalifePassword: formData.herbalifePassword,
        sponsorName: formData.sponsorName.trim(),
        sponsorLastName: formData.sponsorLastName.trim(),
        sponsorId: formData.sponsorId.trim(),
        weight: parseFloat(formData.weight) || 0,
        bmi: parseFloat(formData.bmi) || 0,
        bodyFat: parseFloat(formData.fat) || 0,
        familyDetails: {
          birthday: formData.birthday || "",
          maritalStatus: formData.maritalStatus || "",
          spouseName: formData.maritalStatus === "married" ? formData.spouseName.trim() : "",
          marriageDate: formData.maritalStatus === "married" ? formData.marriageDate : "",
          children: formData.children.map((child) => ({
            name: child.name.trim(),
            gender: child.gender,
            birthday: child.birthday,
          })),
        },
        height: parseFloat(formData.height) || 0,
        inventory: {
          hasPurchasedPackage: false,
          remainingShakes: 0,
          complementaryShakeQuantity: 0
        }
      };

      await dispatch(createUser(userData)).unwrap();

      toast.success("User created successfully!");

      // Send WhatsApp welcome message
      try {
        const whatsappResult = await sendWelcomeMessage(
          formData.mobile,
          formData.name
        );

        if (whatsappResult.success) {
          toast.success("Welcome message sent via WhatsApp!");
        } else {
          console.error("WhatsApp message failed:", whatsappResult.error);
          // toast.error(`WhatsApp failed: ${whatsappResult.error}`);
        }
      } catch (whatsappError) {
        // console.error("WhatsApp error:", whatsappError);
        // toast.error("Failed to send WhatsApp message");
      }

      // Reset form
      setFormData({
        name: "",
        mobile: "",
        whatsappNumber: "",
        address: "",
        email: "",
        password: "",
        confirmPassword: "",
        weight: "",
        fat: "",
        visceralFat: "",
        muscles: "",
        bmr: "",
        bmi: "",
        bodyAge: "",
        chest: "",
        waistLine: "",
        hip: "",
        role: "user",
        customerType: "Trial",
        referredBy: "",
        sponsoredBy: "",
        referralType: "",
        purpose: "",
        herbalifeId: "",
        herbalifeLastName: "",
        herbalifeEmail: "",
        herbalifePassword: "",
        preferredCustomerStatus: "",
        herbalifeJoinYear: "",
        sponsorName: "",
        sponsorLastName: "",
        sponsorId: "",
        birthday: "",
        maritalStatus: "",
        spouseName: "",
        marriageDate: "",
        children: [],
        height: "",
      });

      setTimeout(() => {
        navigate("/admin/add-user");
      }, 1500);
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error || "Failed to create user");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100 p-4 md:p-6 lg:p-8 font-sans text-neutral-800">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-sm p-3 md:p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-neutral-900 tracking-tight">
                Create New User
              </h1>
              <p className="text-xs md:text-base text-neutral-500 mt-1">
                Create a new user profile with health metrics and account
                details.
              </p>
            </div>
            {/* <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="h-10 px-5 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl font-medium text-sm transition-all shadow-lg shadow-neutral-900/10 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Create User
                </>
              )}
            </button> */}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Personal Info & Account */}
          <div className="space-y-6">
            <AdminCard title="Personal Information">
              <div className="p-4 md:p-5 space-y-4">
                <AdminInput
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter full name"
                  error={errors.name}
                  required
                />
                <div>
                  <AdminInput
                    label="Mobile Number"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    placeholder="10 digit mobile number"
                    maxLength={10}
                    error={errors.mobile}
                    required
                  />
                  {mobileExists && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600 font-medium bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                      <span>⚠️</span>
                      <span>This mobile number is already registered</span>
                    </div>
                  )}
                </div>
                <AdminInput
                  label="WhatsApp Number (Optional)"
                  type="tel"
                  value={formData.whatsappNumber}
                  onChange={(e) =>
                    handleChange("whatsappNumber", e.target.value)
                  }
                  placeholder="10 digit WhatsApp number (if different)"
                  maxLength={10}
                  error={errors.whatsappNumber}
                />
                <AdminInput
                  label="Email (Optional)"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="example@email.com"
                  error={errors.email}
                />
                <AdminInput
                  label="Address"
                  type="textarea"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="Enter complete address"
                  error={errors.address}
                />
              </div>
            </AdminCard>

            <AdminCard title="Family & Important Dates">
              <div className="p-4 md:p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminInput
                    label="Birthday"
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => handleChange("birthday", e.target.value)}
                  />
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Marital Status
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "single", label: "Single" },
                        { value: "married", label: "Married" },
                      ]}
                      value={formData.maritalStatus}
                      onChange={(val) => handleChange("maritalStatus", val)}
                      placeholder="Select Status"
                    />
                  </div>
                </div>

                {formData.maritalStatus === "married" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                    <AdminInput
                      label="Spouse Name"
                      value={formData.spouseName}
                      onChange={(e) => handleChange("spouseName", e.target.value)}
                      placeholder="Enter spouse name"
                    />
                    <AdminInput
                      label="Marriage Date"
                      type="date"
                      value={formData.marriageDate}
                      onChange={(e) => handleChange("marriageDate", e.target.value)}
                    />
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Children</h4>
                    <button
                      type="button"
                      onClick={handleAddChild}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Child
                    </button>
                  </div>

                  {formData.children.length === 0 && (
                    <p className="text-xs text-neutral-400 text-center py-3">
                      No children added yet
                    </p>
                  )}

                  <div className="space-y-3">
                    {formData.children.map((child, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-12 gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-200 relative"
                      >
                        <div className="col-span-6">
                          <AdminInput
                            label="Child Name"
                            value={child.name}
                            onChange={(e) => handleChildChange(index, "name", e.target.value)}
                            placeholder="Enter name"
                          />
                        </div>
                        <div className=" col-span-3">
                          <label className="text-sm font-medium text-neutral-700">Gender</label>
                          <CustomDropdown
                            options={[
                              { value: "son", label: "Son" },
                              { value: "daughter", label: "Daughter" },
                            ]}
                            value={child.gender}
                            onChange={(val) => handleChildChange(index, "gender", val)}
                            placeholder="Select"
                          />
                        </div>
                        <div className="col-span-3">
                          <AdminInput
                            label="Birthday"
                            type="date"
                            value={child.birthday}
                            onChange={(e) => handleChildChange(index, "birthday", e.target.value)}
                          />
                        </div>
                        <div className="absolute top-0 right-0">
                          <button
                            type="button"
                            onClick={() => handleRemoveChild(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors mb-0.5"
                            title="Remove child"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AdminCard>

            <AdminCard title="Body Measurements">
              <div className="p-4 md:p-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <AdminInput
                    label="Weight (kg)"
                    type="number"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => handleChange("weight", e.target.value)}
                    error={errors.weight}
                  />
                  <AdminInput
                    label="Body Fat (%)"
                    type="number"
                    step="0.1"
                    value={formData.fat}
                    onChange={(e) => handleChange("fat", e.target.value)}
                  />
                  <AdminInput
                    label="Visceral Fat"
                    type="number"
                    step="0.1"
                    value={formData.visceralFat}
                    onChange={(e) =>
                      handleChange("visceralFat", e.target.value)
                    }
                  />
                  <AdminInput
                    label="Muscle Mass (kg)"
                    type="number"
                    step="0.1"
                    value={formData.muscles}
                    onChange={(e) => handleChange("muscles", e.target.value)}
                  />
                  <AdminInput
                    label="BMR (kcal)"
                    type="number"
                    value={formData.bmr}
                    onChange={(e) => handleChange("bmr", e.target.value)}
                  />
                  <AdminInput
                    label="BMI"
                    type="number"
                    step="0.1"
                    value={formData.bmi}
                    onChange={(e) => handleChange("bmi", e.target.value)}
                  />
                  <AdminInput
                    label="Body Age"
                    type="number"
                    value={formData.bodyAge}
                    onChange={(e) => handleChange("bodyAge", e.target.value)}
                  />
                  <AdminInput
                    label="Chest (in)"
                    type="number"
                    step="0.1"
                    value={formData.chest}
                    onChange={(e) => handleChange("chest", e.target.value)}
                  />
                  <AdminInput
                    label="Waist Line (in)"
                    type="number"
                    step="0.1"
                    value={formData.waistLine}
                    onChange={(e) => handleChange("waistLine", e.target.value)}
                  />

                  <div className="md:col-span-3 lg:col-span-1">
                    <AdminInput
                      label="Hip (in)"
                      type="number"
                      step="0.1"
                      value={formData.hip}
                      onChange={(e) => handleChange("hip", e.target.value)}
                    />
                  </div>
                  <AdminInput
                    label="Height (cm)"
                    type="number"
                    step="0.1"
                    value={formData.height}
                    onChange={(e) => handleChange("height", e.target.value)}
                  />
                </div>

                <div className="mt-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                  <p className="text-xs text-blue-600 leading-relaxed">
                    <strong>Note:</strong> Initial health metrics help provide
                    better recommendations. Verified status and onboarding will
                    be set directly to complete.
                  </p>
                </div>
              </div>
            </AdminCard>
          </div>

          {/* Right Column - Health Metrics */}
          <div className="space-y-6">
            <AdminCard title="Account Security & Role">
              <div className="p-4 md:p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <AdminInput
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      placeholder="Min 6 characters"
                      error={errors.password}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <div className="relative">
                    <AdminInput
                      label="Confirm Password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleChange("confirmPassword", e.target.value)
                      }
                      placeholder="Re-enter password"
                      error={errors.confirmPassword}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Account Role
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "user", label: "User" },
                        { value: "associate", label: "Associate" },
                        { value: "admin", label: "Admin" },
                      ]}
                      value={formData.role}
                      onChange={(val) => handleChange("role", val)}
                      placeholder="Select Role"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Customer Type
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "Registered", label: "Registered" },
                        { value: "Trial", label: "Trial" },
                      ]}
                      value={formData.customerType}
                      onChange={(val) => handleChange("customerType", val)}
                      placeholder="Select Type"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Detailed Purpose
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "Weight Loss", label: "Weight Loss" },
                        { value: "Weight Gain", label: "Weight Gain" },
                        { value: "Child Nutrition", label: "Child Nutrition" },
                        { value: "Skin Care", label: "Skin Care" },
                        { value: "Business Related", label: "Business Related" },
                      ]}
                      value={formData.purpose}
                      onChange={(val) => handleChange("purpose", val)}
                      placeholder="Select Purpose"
                    />
                  </div>
                  <div className="space-y-4">
                    <div className="relative" onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            setShowReferredByDropdown(false);
                        }
                    }}>
                      <div onFocus={() => setShowReferredByDropdown(true)}>
                        <AdminInput
                          label="Referred By"
                          value={formData.referredBy}
                          onChange={(e) => {
                            handleChange("referredBy", e.target.value);
                            setShowReferredByDropdown(true);
                          }}
                          placeholder="Search or enter referrer name"
                        />
                      </div>
                      {showReferredByDropdown && getFilteredUsers(formData.referredBy).length > 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-48 overflow-y-auto">
                          {getFilteredUsers(formData.referredBy).map(u => (
                             <button
                               key={u.id}
                               type="button"
                               className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                               onClick={() => {
                                 handleChange("referredBy", u.name);
                                 setShowReferredByDropdown(false);
                               }}
                             >
                                <div className="font-medium text-sm text-gray-900">{u.name}</div>
                                <div className="text-xs text-gray-500">{u.mobile} • {u.customerNumber || u.role}</div>
                             </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="relative" onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            setShowSponsoredByDropdown(false);
                        }
                    }}>
                      <div onFocus={() => setShowSponsoredByDropdown(true)}>
                        <AdminInput
                          label="Sponsored By"
                          value={formData.sponsoredBy}
                          onChange={(e) => {
                            handleChange("sponsoredBy", e.target.value);
                            setShowSponsoredByDropdown(true);
                          }}
                          placeholder="Search or enter sponsor name"
                        />
                      </div>
                      {showSponsoredByDropdown && getFilteredUsers(formData.sponsoredBy).length > 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-48 overflow-y-auto">
                          {getFilteredUsers(formData.sponsoredBy).map(u => (
                             <button
                               key={u.id}
                               type="button"
                               className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                               onClick={() => {
                                 handleChange("sponsoredBy", u.name);
                                 setShowSponsoredByDropdown(false);
                               }}
                             >
                                <div className="font-medium text-sm text-gray-900">{u.name}</div>
                                <div className="text-xs text-gray-500">{u.mobile} • {u.customerNumber || u.role}</div>
                             </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div class="space-y-2">
                    <AdminInput
                      value={formData.referralType}
                      onChange={(e) => handleChange("referralType", e.target.value)}
                      placeholder="e.g., Friend, Family, Social Media"
                    />
                  </div>
                </div>
              </div>
            </AdminCard>
            <AdminCard title="Herbalife Details">
              <div className="p-4 md:p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminInput
                    label="Herbalife ID"
                    value={formData.herbalifeId}
                    onChange={(e) => handleChange("herbalifeId", e.target.value)}
                    placeholder="Enter ID"
                  />
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Preferred Status
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "Coach", label: "Coach" },
                        { value: "President", label: "President" },
                        { value: "Ambassador", label: "Ambassador" },
                      ]}
                      value={formData.preferredCustomerStatus}
                      onChange={(val) => handleChange("preferredCustomerStatus", val)}
                      placeholder="Select Status"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminInput
                    label="Herbalife Last Name"
                    value={formData.herbalifeLastName}
                    onChange={(e) => handleChange("herbalifeLastName", e.target.value)}
                    placeholder="Enter Last Name"
                  />
                  <AdminInput
                    label="Join Year"
                    value={formData.herbalifeJoinYear}
                    onChange={(e) => handleChange("herbalifeJoinYear", e.target.value)}
                    placeholder="YYYY"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminInput
                    label="Herbalife Email"
                    type="email"
                    value={formData.herbalifeEmail}
                    onChange={(e) => handleChange("herbalifeEmail", e.target.value)}
                    placeholder="herbalife@email.com"
                  />
                  <AdminInput
                    label="Herbalife Password"
                    value={formData.herbalifePassword}
                    onChange={(e) => handleChange("herbalifePassword", e.target.value)}
                    placeholder="Enter Password"
                  />
                </div>
                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Sponsor Details</h4>
                  <div className="space-y-4">
                    <AdminInput
                      label="Sponsor Name"
                      value={formData.sponsorName}
                      onChange={(e) => handleChange("sponsorName", e.target.value)}
                      placeholder="Sponsor Full Name"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <AdminInput
                        label="Sponsor Last Name"
                        value={formData.sponsorLastName}
                        onChange={(e) => handleChange("sponsorLastName", e.target.value)}
                        placeholder="Sponsor Last Name"
                      />
                      <AdminInput
                        label="Sponsor ID"
                        value={formData.sponsorId}
                        onChange={(e) => handleChange("sponsorId", e.target.value)}
                        placeholder="Sponsor ID"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </AdminCard>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="h-10 px-5 py-6 bg-red-700  text-white font-medium text-sm transition-all shadow-lg shadow-neutral-900/10 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed w-full"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Create User
                </>
              )}
            </button>
          </div>
        </div>
      </div >
    </div >
  );
};

export default CreateUser;

